/**
 * Sprint 5 client feedback 2026-07-07 (UI Issues #2) — a reusable
 * table-cell wrapper that clamps long text to two lines with an
 * ellipsis, and reveals a "View" link that opens the full content in
 * a lightweight modal.
 *
 * Design constraints (from the client ticket):
 *   * Clamp to a max of 2 lines with "…" overflow.
 *   * "View" link ONLY visible when the content is actually truncated
 *     (measured, not assumed from length).
 *   * Popup uses the existing modal / overlay pattern (see
 *     InvestmentTypeModal for the reference shell).
 *   * Modal ≈700-800px max width; scrollable body for very long
 *     content; close via ✕, ESC, or backdrop click.
 *   * Modal is only mounted when opened — no per-row popup instances
 *     on initial render.
 *
 * The wrapper accepts either a plain `text` string OR arbitrary
 * `children` (e.g. a <FormattedText/> node that renders markdown
 * bold spans). Both surfaces the same truncation + modal behaviour.
 */

import {
  type JSX,
  type ReactNode,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import { FormattedText } from './FormattedText';

interface TruncatedCellProps {
  /** Plain-text version used for measurement AND as the modal body
   *  when `children` is not provided. */
  text?: string | null;
  /** Optional rich content — rendered inside the clamp AND inside
   *  the modal body. When both `text` and `children` are supplied,
   *  `children` is rendered but `text` is still used for measurement
   *  (character-count sanity check). */
  children?: ReactNode;
  /** Modal title. Defaults to "Details". */
  title?: string;
  /** Optional className on the outer wrapper (e.g. cell modifiers). */
  className?: string;
  /** Optional line clamp override. Defaults to 2 per the spec. */
  lines?: number;
  /** Sprint 5 client feedback 2026-07-10 (Iter1 §7) — force the
   *  "View" button visible regardless of overflow. Useful when the
   *  clamped text is short (e.g. an assumption title) but the modal
   *  body carries extra detail (rationale + stated value) the
   *  analyst still needs to see. */
  alwaysShowView?: boolean;
  /** Sprint 5 client feedback 2026-07-11 (Iter4b §7) — when the
   *  inline `children` are a short title (e.g. the assumption name)
   *  and the caller wants the modal to open with a RICHER body
   *  (description + rationale + benchmark + variance + citation),
   *  pass that longer body here. Falls back to `text` if omitted,
   *  which in turn falls back to `children`. Used by §7 Key
   *  Assumptions so the View modal expands what the clamp shows. */
  modalBody?: ReactNode;
}

/**
 * Render the truncated cell.
 * Behaviour:
 *   1. Content lives inside a div with `-webkit-line-clamp: N`.
 *   2. After render, useLayoutEffect measures scrollHeight vs
 *      clientHeight. If overflowed, `truncated` flag flips true and
 *      the "View" link renders.
 *   3. Window resize re-measures via a ResizeObserver so column-width
 *      changes don't leave stale View links visible.
 *   4. The modal is React-mounted only when `open` — no cost per row
 *      on initial render (per client "Render dialogs only when needed").
 */
export function TruncatedCell({
  text,
  children,
  title = 'Details',
  className,
  lines = 2,
  alwaysShowView = false,
  modalBody: modalBodyProp,
}: TruncatedCellProps): JSX.Element {
  const clampRef = useRef<HTMLDivElement | null>(null);
  const [truncated, setTruncated] = useState(false);
  const [open, setOpen] = useState(false);

  const measure = useCallback(() => {
    const el = clampRef.current;
    if (!el) return;
    // scrollHeight includes overflowed content; when it exceeds
    // clientHeight (limited by the line-clamp) we know the box is
    // clipping the content.
    setTruncated(el.scrollHeight - el.clientHeight > 1);
  }, []);

  useLayoutEffect(() => {
    measure();
  }, [measure, text, children]);

  useEffect(() => {
    const el = clampRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [measure]);

  const clampStyle: React.CSSProperties = {
    display: '-webkit-box',
    WebkitLineClamp: lines,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
    // Ensures the line-clamp math kicks in on wide cells too.
    wordBreak: 'break-word',
    // Sprint 5 client feedback 2026-07-08 (UI Ticket — View button
    // alignment) — flex:1 lets the clamped text absorb the row's
    // remaining width so the View button sits on its right, not
    // below. min-width:0 is required inside a flex row for
    // -webkit-line-clamp to actually clip; without it the flex
    // child claims its intrinsic content width and the ellipsis
    // never appears.
    flex: '1 1 auto',
    minWidth: 0,
  };

  /* Sprint 5 client feedback 2026-07-11 (Format Phase 1) — dozens of
     callers pass `text` as a raw LLM string that contains **bold** /
     *italic* / HTML entities. Prior to this fix the fallback rendered
     the raw string verbatim, so users saw literal ** characters in
     Mitigation / Description / Evidence cells. Routing the text-only
     fallback through <FormattedText inline /> renders markdown as
     JSX for every text-only caller in one edit. Plain strings pass
     through as a no-op — only strings containing markers change. */
  const body: ReactNode = children ?? (
    text != null && text !== '' ? <FormattedText text={text} inline /> : ''
  );
  /* Iter4b §7 — when the caller explicitly passes `modalBody`, use
     that for the modal so short inline titles can open a richer
     detail view. Otherwise preserve the existing fallback order
     (children preferred, then text). Modal body gets block-level
     formatting (not inline) so paragraph breaks and bullet lists
     render correctly in the popup. */
  const modalBody: ReactNode = modalBodyProp ?? children ?? (
    text != null && text !== '' ? <FormattedText text={text} /> : ''
  );

  return (
    /* Sprint 5 client feedback 2026-07-08 (UI Ticket — View button
       alignment) — cell wrapper is a flex row so "View" sits INSIDE
       the same cell to the right of the clamped text, vertically
       centered. Prior layout stacked the button on a new line below
       the text which pushed row height up and broke visual rhythm
       across tables. */
    <div
      className={`truncated-cell${className ? ` ${className}` : ''}`}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 'var(--s2)',
        width: '100%',
      }}
    >
      <div ref={clampRef} style={clampStyle}>
        {body}
      </div>
      {(truncated || alwaysShowView) && (
        <button
          type="button"
          className="truncated-cell-view"
          onClick={() => setOpen(true)}
          aria-label={`View full ${title}`}
        >
          View
        </button>
      )}
      {open && (
        <CellDetailModal title={title} onClose={() => setOpen(false)}>
          {modalBody}
        </CellDetailModal>
      )}
    </div>
  );
}


/**
 * Lightweight modal used by TruncatedCell. Not exported from the
 * main components barrel — only meant to be rendered from
 * TruncatedCell (or another truncation-style wrapper) to keep the
 * modal pattern consistent.
 */
interface CellDetailModalProps {
  title: string;
  onClose: () => void;
  children: ReactNode;
}

function CellDetailModal({
  title,
  onClose,
  children,
}: CellDetailModalProps): JSX.Element {
  // Close on ESC — spec explicitly asks for this.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    // Lock scroll on the document body while the modal is open so
    // the analyst doesn't accidentally scroll the underlying page.
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [onClose]);

  /* Rendered into <body>, NOT in place.
   *
   * This modal used to render where it sat — deep inside a report table, inside the
   * collapsible report panels. `position: fixed` only escapes to the viewport if NO
   * ancestor establishes a containing block, and any ancestor with a transform,
   * filter, backdrop-filter, will-change or contain does exactly that. The report page
   * is full of such ancestors, so the overlay was being trapped and clipped inside its
   * own section: the analyst clicked "View" on Market Performance and nothing appeared
   * (until an unrelated repaint — moving the cursor to the browser chrome — flushed it
   * into view).
   *
   * A portal removes the entire class of bug rather than chasing whichever ancestor
   * happens to trap it today. TooltipHost already does this, for exactly this reason;
   * this modal was the last overlay still rendering in place. */
  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="truncated-modal-title"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 1000,
        background: 'rgba(0,0,0,0.45)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 'var(--s4)',
      }}
      onClick={onClose}
    >
      <div
        className="card"
        style={{
          width: 'min(760px, 92vw)',
          maxHeight: 'min(85vh, 720px)',
          display: 'flex',
          flexDirection: 'column',
          background: 'var(--bg)',
          boxShadow: '0 8px 32px rgba(0,0,0,0.24)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          className="card-header"
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: 'var(--s3) var(--s4)',
            borderBottom: '1px solid var(--lines)',
          }}
        >
          <h3 id="truncated-modal-title" className="t-h3" style={{ margin: 0 }}>
            {title}
          </h3>
          <button
            type="button"
            className="truncated-cell-close"
            onClick={onClose}
            aria-label="Close"
          >
            ×
          </button>
        </div>
        <div
          style={{
            padding: 'var(--s3) var(--s4)',
            overflowY: 'auto',
            flex: 1,
            fontSize: '0.9375rem',
            lineHeight: 1.6,
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
          }}
        >
          {children}
        </div>
      </div>
    </div>,
    document.body,
  );
}
